// Core
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import {
  AnchorNav,
  Button,
  Chip,
  FieldSearchSelect,
  FieldSelect,
  FieldText,
  FieldToggle,
  SectionCard,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import BusinessValidityChangeNotice from "components/BusinessValidityChangeNotice";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";

// Utils
import { ciclesTypes, contractTypes, contractedPowerOptions } from "utils/constants/index";
import { formatDate } from "utils/time";

const K_ROWS = ["k1", "k2", "k5", "k6"];
const PERIODS = [
  { key: "peak_value", labelKey: "single.koefficients.table.columns.ponta" },
  { key: "mid_peak_value", labelKey: "single.koefficients.table.columns.cheias" },
  { key: "off_peak_value", labelKey: "single.koefficients.table.columns.vazio" },
  { key: "super_off_peak_value", labelKey: "single.koefficients.table.columns.super_vazio" },
];

const BTN_LOW = ["BTN simples", "BTN bi-horária", "BTN< tri-horária"];

function InfoNote({ children }) {
  return (
    <div
      className="flex items-start gap-[10px] text-[12.5px] whitespace-pre-line"
      style={{
        padding: "12px 14px",
        background: "var(--surface-2)",
        borderRadius: "var(--radius-sm)",
        color: "var(--ink-2)",
        lineHeight: 1.55,
      }}
    >
      <Icon name="info" style={{ color: "var(--accent)", fontSize: 18, flexShrink: 0 }} />
      <div>{children}</div>
    </div>
  );
}

InfoNote.propTypes = {
  children: PropTypes.node.isRequired,
};

/* One tariff side (buy or sell): inline type select in the section header,
   per-type fields below — mirrors the design's ContractEditor "Tarifário". */
function TariffSection({
  id,
  title,
  type,
  payload,
  onChangeType,
  onChangePayload,
  disabled,
  side,
}) {
  const { t } = useTranslation(["contracts"]);

  const setP = (field) => (v) => onChangePayload(field, v);

  return (
    <SectionCard
      id={id}
      icon="request_quote"
      title={title}
      action={
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] font-semibold text-ink-3">{t("form.contract_type")}</span>
          <select
            className="rec-input !w-auto min-w-[180px] !h-[36px]"
            value={type}
            disabled={disabled}
            onChange={(e) => onChangeType(e.target.value)}
          >
            {contractTypes.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`options.types.${o.value}`, { defaultValue: o.label })}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {type === "NO_VALUES" && <InfoNote>{t("single.no_values_note")}</InfoNote>}

      {type === "FIXED" && payload && (
        <>
          <div className="text-[12.5px] font-bold text-ink-2 mb-3">
            {t("single.fixed.values_title")}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-[14px]">
            {PERIODS.map((p) => (
              <FieldText
                key={p.key}
                label={t(p.labelKey)}
                type="number"
                suffix="€"
                disabled={disabled}
                value={payload[p.key] ?? 0}
                onChange={setP(p.key)}
              />
            ))}
          </div>
          <div className="rec-divider" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldText
              label={t("single.fixed.multiplier_pmd")}
              type="number"
              disabled={disabled}
              value={payload.multiplierPMD ?? 1}
              onChange={setP("multiplierPMD")}
            />
            <FieldText
              label={t("single.fixed.multiplier_erse")}
              type="number"
              disabled={disabled}
              value={payload.multiplierERSE ?? 1}
              onChange={setP("multiplierERSE")}
            />
          </div>
          <div className="mt-4">
            <InfoNote>
              <b>{t("single.fixed.formula.title")}</b>
              {"\n"}
              {side === "buy"
                ? t("single.fixed.formula.body_buy")
                : t("single.fixed.formula.body_sell")}
            </InfoNote>
          </div>
        </>
      )}

      {type === "INDEXED_SIMPLIFIED" && payload && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FieldText
              label={t("single.fixed.multiplier_pmd")}
              type="number"
              disabled={disabled}
              value={payload.multiplierPMD ?? 1}
              onChange={setP("multiplierPMD")}
            />
            <FieldText
              label={t("single.fixed.multiplier_erse")}
              type="number"
              disabled={disabled}
              value={payload.multiplierERSE ?? 1}
              onChange={setP("multiplierERSE")}
            />
            <FieldText
              label={t("single.indexed_simple.additive_c")}
              type="number"
              suffix="€"
              disabled={disabled}
              value={payload.additiveC ?? 0}
              onChange={setP("additiveC")}
            />
          </div>
          <div className="mt-4">
            <InfoNote>
              <b>{t("single.indexed_simple.formula_title")}</b>{" "}
              {t("single.indexed_simple.formula_inline")}
              {"\n"}
              {t("single.indexed_simple.bullets.A")}
              {"\n"}
              {t("single.indexed_simple.bullets.B")}
              {"\n"}
              {t("single.indexed_simple.bullets.C")}
            </InfoNote>
          </div>
        </>
      )}

      {type === "INDEXED" && payload && (
        <>
          <div className="rec-tbl-wrap">
            <table className="rec-tbl min-w-[460px]">
              <thead>
                <tr>
                  <th>{t("single.koefficients.table.columns.type_k")}</th>
                  {PERIODS.map((p) => (
                    <th key={p.key} style={{ textAlign: "right" }}>
                      {t(p.labelKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {K_ROWS.map((k) => (
                  <tr key={k}>
                    <td className="font-semibold text-ink">{k.toUpperCase()}</td>
                    {PERIODS.map((p) => (
                      <td key={p.key} style={{ padding: "6px 8px" }}>
                        <input
                          className="rec-input !h-[34px] text-right"
                          type="number"
                          aria-label={`${k.toUpperCase()} ${t(p.labelKey)}`}
                          disabled={disabled}
                          value={payload[`${k}_${p.key}`] ?? 0}
                          onChange={(e) => onChangePayload(`${k}_${p.key}`, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <InfoNote>
              <b>{t("single.koefficients.formula.title")}</b>
              {"\n"}
              {side === "buy"
                ? t("single.koefficients.formula.body_buy")
                : t("single.koefficients.formula.body_sell")}
            </InfoNote>
          </div>
        </>
      )}
    </SectionCard>
  );
}

TariffSection.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  type: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.object,
  onChangeType: PropTypes.func.isRequired,
  onChangePayload: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  side: PropTypes.oneOf(["buy", "sell"]).isRequired,
};

TariffSection.defaultProps = {
  payload: null,
};

function ContractCard({
  action,
  contract,
  meterIdentifier,
  meterId,
  memberOptions,
  memberId,
  onChangeMember,
  startDate,
  endDate,
  originalStartDate,
  originalEndDate,
  onChangeStartDate,
  onChangeEndDate,
  buyType,
  sellType,
  onChangeBuyType,
  onChangeSellType,
  voltageLevelOptions,
  voltageLevel,
  onChangeVoltageLevel,
  tariffCycle,
  onChangeTariffCycle,
  withHolidays,
  onChangeWithHolidays,
  contractedPower,
  onChangeContractedPower,
  buyPayload,
  sellPayload,
  onChangeBuyPayload,
  onChangeSellPayload,
  canConfirm,
  onConfirm,
}) {
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["contracts", "meters", "community", "common"]);

  const isView = action === "view";

  let title = t("header.view_title", {
    date: formatDate(contract?.start_date || contract?.validity, ""),
  });
  if (action === "new") title = t("header.new_title");
  if (action === "edit") title = t("header.edit_title");

  const generalLabel = t("sidenav.general_info", {
    ns: "community",
    defaultValue: "Informação Geral",
  });

  const navItems = [
    { id: "general", icon: "badge", label: generalLabel },
    { id: "buy", icon: "shopping_cart", label: t("form.buy_details_title") },
    { id: "sell", icon: "sell", label: t("form.sell_details_title") },
  ];

  const backToMeter = () =>
    navigate(`/communities/${community.id}/infrastructure/meters/${meterId}`);

  // Contracted power input: fixed BTN tiers use a select, free levels a number
  const isLowBtn = BTN_LOW.includes(voltageLevel);
  const isHighBtn = voltageLevel === "BTN>";
  const powerOptions = isLowBtn
    ? contractedPowerOptions.filter((o) => o.value <= 20.7)
    : contractedPowerOptions.filter((o) => o.value > 20.7);

  return (
    <div>
      <div className="rec-crumbs">
        <Icon name="home" />
        <span>{community?.name || "—"}</span>
        <Icon name="chevron_right" />
        <button
          type="button"
          className="bg-transparent border-0 p-0 cursor-pointer text-inherit font-sans text-[12.5px]"
          onClick={() => navigate(`/communities/${community.id}/infrastructure/meters`)}
        >
          {t("title", { ns: "meters" })}
        </button>
        <Icon name="chevron_right" />
        <button
          type="button"
          className="bg-transparent border-0 p-0 cursor-pointer text-inherit font-sans text-[12.5px]"
          onClick={backToMeter}
        >
          {meterIdentifier || "—"}
        </button>
        <Icon name="chevron_right" />
        <b>{title}</b>
      </div>

      <div className="rec-page-head">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <Icon name="arrow_back" />
          {t("footer.back")}
        </Button>
        {!isView && (
          <Button disabled={!canConfirm} onClick={onConfirm}>
            <Icon name="check" />
            {action === "new" ? t("footer.create") : t("footer.edit")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] items-start gap-rec">
        <AnchorNav items={navItems} />

        <div className="flex flex-col gap-rec min-w-0">
          {/* hero */}
          <div className="rec-card rec-card-pad">
            <div className="rec-profile-hero">
              <div
                className="rec-av-lg"
                style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
              >
                <Icon name="receipt_long" fill style={{ fontSize: 30 }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-[10px] flex-wrap">
                  <h2 className="m-0 text-[21px] font-bold text-ink tracking-[-0.3px]">{title}</h2>
                  <Chip tone="teal">
                    {t(`options.types.${buyType}`, { defaultValue: buyType })}
                  </Chip>
                  <Chip tone="info">
                    {t(`options.types.${sellType}`, { defaultValue: sellType })}
                  </Chip>
                </div>
                <div className="mt-[6px] text-ink-3 text-[13.5px] inline-flex items-center gap-[6px]">
                  <Icon name="electric_meter" style={{ fontSize: 16 }} />
                  <span className="font-[family-name:var(--mono)]">{meterIdentifier || "—"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* general */}
          <SectionCard id="general" icon="badge" title={generalLabel}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldSearchSelect
                label={t("form.member_responsible")}
                value={memberId}
                options={[{ value: "", label: "—" }, ...(memberOptions ?? [])]}
                onChange={onChangeMember}
                disabled={isView}
              />
              <ValidityDateRangePicker
                value={{ start: startDate, end: endDate }}
                onChange={({ start, end }) => {
                  onChangeStartDate(start);
                  onChangeEndDate(end);
                }}
                disabled={isView || action === "edit"}
              />
              <FieldSelect
                label={t("form.voltage_level")}
                value={voltageLevel}
                options={[{ value: "", label: "—" }, ...voltageLevelOptions]}
                onChange={onChangeVoltageLevel}
                disabled={isView}
              />
              <FieldSelect
                label={t("form.tariff_cycle")}
                value={tariffCycle}
                options={[
                  { value: "", label: "—" },
                  ...ciclesTypes.map((o) => ({
                    value: o.value,
                    label: t(`options.cicles.${o.value}`, { defaultValue: o.label }),
                  })),
                ]}
                onChange={onChangeTariffCycle}
                disabled={isView}
              />
              {isLowBtn || isHighBtn ? (
                <FieldSelect
                  label={t("form.contracted_power")}
                  value={String(contractedPower)}
                  options={powerOptions.map((o) => ({ value: String(o.value), label: o.label }))}
                  onChange={(v) => onChangeContractedPower(parseFloat(v))}
                  disabled={isView}
                />
              ) : (
                <FieldText
                  label={t("form.contracted_power")}
                  type="number"
                  value={contractedPower}
                  onChange={(v) => onChangeContractedPower(parseFloat(v) || 0)}
                  disabled={isView}
                />
              )}
            </div>
            <div className="mt-4 max-w-[420px]">
              <FieldToggle
                label={t("form.with_holidays")}
                value={withHolidays}
                onChange={onChangeWithHolidays}
                disabled={isView}
              />
            </div>
            <BusinessValidityChangeNotice
              enabled={action === "edit"}
              originalStartDate={originalStartDate}
              originalEndDate={originalEndDate}
              startDate={startDate}
              endDate={endDate}
              className="mt-4"
            />
          </SectionCard>

          <TariffSection
            id="buy"
            side="buy"
            title={t("form.buy_details_title")}
            type={buyType}
            payload={buyPayload}
            onChangeType={onChangeBuyType}
            onChangePayload={onChangeBuyPayload}
            disabled={isView}
          />

          <TariffSection
            id="sell"
            side="sell"
            title={t("form.sell_details_title")}
            type={sellType}
            payload={sellPayload}
            onChangeType={onChangeSellType}
            onChangePayload={onChangeSellPayload}
            disabled={isView}
          />
        </div>
      </div>
    </div>
  );
}

ContractCard.defaultProps = {
  contract: null,
  meterIdentifier: "",
  memberOptions: [],
  memberId: "",
  buyPayload: null,
  sellPayload: null,
  originalStartDate: "",
  originalEndDate: "",
};

ContractCard.propTypes = {
  action: PropTypes.string.isRequired,
  contract: PropTypes.shape({
    validity: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
  meterIdentifier: PropTypes.string,
  meterId: PropTypes.string.isRequired,
  memberOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  memberId: PropTypes.string,
  onChangeMember: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  originalStartDate: PropTypes.string,
  originalEndDate: PropTypes.string,
  onChangeStartDate: PropTypes.func.isRequired,
  onChangeEndDate: PropTypes.func.isRequired,
  buyType: PropTypes.string.isRequired,
  sellType: PropTypes.string.isRequired,
  onChangeBuyType: PropTypes.func.isRequired,
  onChangeSellType: PropTypes.func.isRequired,
  voltageLevelOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  voltageLevel: PropTypes.string.isRequired,
  onChangeVoltageLevel: PropTypes.func.isRequired,
  tariffCycle: PropTypes.string.isRequired,
  onChangeTariffCycle: PropTypes.func.isRequired,
  withHolidays: PropTypes.bool.isRequired,
  onChangeWithHolidays: PropTypes.func.isRequired,
  contractedPower: PropTypes.number.isRequired,
  onChangeContractedPower: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  buyPayload: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  sellPayload: PropTypes.object,
  onChangeBuyPayload: PropTypes.func.isRequired,
  onChangeSellPayload: PropTypes.func.isRequired,
  canConfirm: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ContractCard;
